// PCC_PORTFOLIO_BUBBLE_ANALYSIS_V1_3_1
import React, { useEffect, useMemo, useState } from "react";
import "./PortfolioBubbleAnalysisView.css";

// PCC_PORTFOLIO_BUBBLE_ANALYSIS_V1_1

const STAGES = [
  { key: "OPORTUNIDAD", label: "Prosp.", full: "Prospecto / Oportunidad" },
  { key: "CALIFICACION", label: "Calif.", full: "Calificación" },
  { key: "ANALISIS", label: "Present.", full: "Análisis / Presentación" },
  { key: "PROPUESTA", label: "Prop.", full: "Propuesta / Cotización" },
  { key: "CONTRATACION", label: "Contrat.", full: "Proceso de contratación" },

  { key: "EJECUCION", label: "Ejecución", full: "En ejecución" },
  { key: "CERRADO", label: "Cerrado", full: "Cerrado" },
];

const ROI_BANDS = [
  { key: "NO_DATA", label: "Sin datos", color: "#a8aaa6" },
  { key: "NEGATIVE", label: "ROI ≤ 0", color: "#df474c" },
  { key: "LOW", label: "0–10 %", color: "#e49a19" },
  { key: "MEDIUM", label: "10–18 %", color: "#69aa54" },
  { key: "HIGH", label: "> 18 %", color: "#16967e" },
];

function n(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalize(value) {
  return String(value || "").trim().toUpperCase();
}

function getProjectId(project) {
  return (
    project?.id ||
    project?.proyectoId ||
    String(project?.pk || "").replace(/^PROJECT#/, "")
  );
}

function getSituation(project) {
  return normalize(project?.situacionProyecto);
}

// PCC_BUBBLE_STAGES_FIX_V1
function getStageKey(project) {
  const situation = getSituation(project);

  if (situation === "EJECUCION") {
    return "EJECUCION";
  }

  if (
    situation === "CERRADO" ||
    situation === "CERRADO_SATISFACTORIAMENTE" ||
    situation === "MUERTO"
  ) {
    return "CERRADO";
  }

  const commercial = normalize(
    project?.etapaComercial
  );

  if (commercial === "GANADO") {
    return "CERRADO";
  }

  if (
    STAGES.some(
      (stage) => stage.key === commercial
    )
  ) {
    return commercial;
  }

  return "SIN_ETAPA";
}

function getStageIndex(project) {
  const key = getStageKey(project);

  const index = STAGES.findIndex(
    (stage) => stage.key === key
  );

  return index >= 0 ? index : -1;
}

function getAmount(project) {
  const situation = getSituation(project);

  if (situation === "VENTA_ACTIVA") {
    return Math.max(
      0,
      n(
        project?.montoOportunidad ??
        project?.montoBaseTamano
      )
    );
  }

  return Math.max(
    0,
    n(
      project?.montoContratado ??
      project?.montoContrato ??
      project?.montoBaseTamano ??
      project?.montoOportunidad
    )
  );
}

// FIN022C_EXCEL_VALIDATION
function calculateRoi(financial) {
  if (!financial || typeof financial !== "object") {
    return null;
  }

  const rawInvested =
    financial.costoNeto ??
    financial.invertido ??
    financial.costos;

  const rawUtility = financial.utilidad;

  if (
    rawInvested === null ||
    rawInvested === undefined ||
    rawInvested === "" ||
    rawUtility === null ||
    rawUtility === undefined ||
    rawUtility === ""
  ) {
    return null;
  }

  const invested = Number(rawInvested);
  const utility = Number(rawUtility);

  if (
    !Number.isFinite(invested) ||
    !Number.isFinite(utility) ||
    invested <= 0
  ) {
    return null;
  }

  return (utility / invested) * 100;
}

function getRoiBand(roi) {
  if (roi === null || roi === undefined) {
    return ROI_BANDS[0];
  }

  const value = n(roi);

  if (value <= 0) return ROI_BANDS[1];
  if (value <= 10) return ROI_BANDS[2];
  if (value <= 18) return ROI_BANDS[3];

  return ROI_BANDS[4];
}

function clamp(value, min, max) {
  return Math.min(
    max,
    Math.max(min, value)
  );
}

function formatMdp(value) {
  return (
    "$" +
    (n(value) / 1000000).toLocaleString(
      "es-MX",
      {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      }
    ) +
    " mdp"
  );
}

function formatMoney(value) {
  return n(value).toLocaleString(
    "es-MX",
    {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    }
  );
}

function formatPercent(value) {
  if (value === null || value === undefined) {
    return "Sin datos";
  }

  return n(value).toFixed(1) + "%";
}

function bubbleRadius(amount, maxAmount) {
  if (amount <= 0 || maxAmount <= 0) {
    return 16;
  }

  const relative = Math.sqrt(
    amount / maxAmount
  );

  return 18 + relative * 48;
}

export default function PortfolioBubbleAnalysisView({
  proyectos = [],
  apiRequest,
  onBack,
}) {
  const [financeById, setFinanceById] = useState({});
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState("");
  // PCC_BUBBLE_UX_V2_2
  const [selectedId, setSelectedId] = useState("");

  // UX023A_EXECUTIVE_MODAL
  const [executiveTab, setExecutiveTab] = useState("resumen");

  const [executiveMovements, setExecutiveMovements] =
    useState({
      loading: false,
      rows: [],
      error: null
    });

  useEffect(() => {
    let cancelled = false;

    async function loadFinance() {
      setLoading(true);

      if (typeof apiRequest !== "function") {
        setLoading(false);
        return;
      }

      // PCC_BUBBLE_FINANCE_FIX_V1
      // Carga financiera con concurrencia limitada.
      const queue = (proyectos || [])
        .map((project) => ({
          id: getProjectId(project),
          project,
        }))
        .filter((item) => Boolean(item.id));

      const rows = [];
      const concurrency = 3;
      let cursor = 0;

      async function worker() {
        while (!cancelled) {
          const index = cursor;
          cursor += 1;

          if (index >= queue.length) {
            return;
          }

          const item = queue[index];

          try {
            const response = await apiRequest(
              "/proyectos/" +
                encodeURIComponent(item.id) +
                "/finanzas/resumen",
              {
                method: "GET",
              }
            );

            if (cancelled) return;

            const summary =
                response?.resumen &&
                typeof response.resumen === "object" &&
                !Array.isArray(response.resumen)
                  ? response.resumen
                  : null;

              rows.push([item.id, summary]);
          } catch (error) {
            console.warn(
              "Resumen financiero no disponible:",
              item.id,
              error
            );

            rows.push([item.id, null]);
          }
        }
      }

      const workers = Array.from(
        {
          length: Math.min(
            concurrency,
            queue.length
          ),
        },
        () => worker()
      );

      await Promise.all(workers);

      if (cancelled) {
        return;
      }

      const map = {};

      rows.forEach((row) => {
        if (!row) {
          return;
        }

        map[row[0]] = row[1] ?? null;
      });

      setFinanceById(map);
      setLoading(false);
    }

    loadFinance();

    return () => {
      cancelled = true;
    };
  }, [proyectos, apiRequest]);


  // UX023A_EXECUTIVE_MODAL_LOADER
  useEffect(() => {
    if (!selectedId) {
      setExecutiveTab("resumen");
      setExecutiveMovements({
        loading: false,
        rows: [],
        error: null
      });
      return;
    }

    let alive = true;

    setExecutiveTab("resumen");

    setExecutiveMovements({
      loading: true,
      rows: [],
      error: null
    });

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSelectedId("");
      }
    };

    window.addEventListener("keydown", handleEscape);

    async function loadMovements() {
      try {
        if (typeof apiRequest !== "function") {
          throw new Error("Servicio API no disponible.");
        }

        const response = await apiRequest(
          "/proyectos/" +
          encodeURIComponent(selectedId) +
          "/finanzas/movimientos",
          { method: "GET" }
        );

        if (!alive) return;

        const candidates = [
          response?.items,
          response?.movimientos,
          response?.records,
          response?.data?.items,
          response?.data?.movimientos
        ];

        const rows = candidates.find(Array.isArray);

        if (!rows) {
          throw new Error(
            "El endpoint no devolvio una coleccion reconocida."
          );
        }

        setExecutiveMovements({
          loading: false,
          rows,
          error: null
        });

        console.info("IP_DEPOT_UX023A_MOVEMENTS", {
          projectId: selectedId,
          count: rows.length
        });
      } catch (error) {
        if (!alive) return;

        setExecutiveMovements({
          loading: false,
          rows: [],
          error:
            error?.message ||
            "No fue posible consultar los movimientos."
        });
      }
    }

    loadMovements();

    return () => {
      alive = false;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [selectedId, apiRequest]);

  const executiveMoney = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      !Number.isFinite(Number(value))
    ) {
      return "Sin datos";
    }

    return formatMoney(Number(value));
  };

  const executivePercent = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      !Number.isFinite(Number(value))
    ) {
      return "Sin datos";
    }

    return formatPercent(Number(value));
  };

  const executiveValue = (value) =>
    value === null ||
    value === undefined ||
    value === ""
      ? "No registrado"
      : String(value);

  const bubbles = useMemo(() => {
    const validProjects = (proyectos || []).filter(
      (project) =>
        getProjectId(project) &&
        getStageIndex(project) >= 0
    );

    const maxAmount = validProjects.reduce(
      (max, project) =>
        Math.max(
          max,
          getAmount(project)
        ),
      0
    );

    return validProjects.map((project) => {
      const id = getProjectId(project);
      const financial = financeById[id] || {};

      const amount = getAmount(project);
      const roi = calculateRoi(financial);
      const rawCollected = financial?.avanceCobro;

      const hasCollected =
        rawCollected !== null &&
        rawCollected !== undefined &&
        rawCollected !== "" &&
        Number.isFinite(Number(rawCollected));

      // Cero se utiliza solo como coordenada tecnica
      // de la burbuja cuando no existe avance.
      // Nunca se presenta como porcentaje validado.
      const collected = hasCollected
        ? clamp(Number(rawCollected), 0, 100)
        : 0;

      return {
        id,
        project,
        financial,
        amount,
        roi,
        collected,
        hasCollected,
        stageIndex: getStageIndex(project),
        band: getRoiBand(roi),
        radius: bubbleRadius(
          amount,
          maxAmount
        ),
      };
    });
  }, [proyectos, financeById]);

  const hovered = bubbles.find(
    (bubble) => bubble.id === hoveredId
  );

  const selected = bubbles.find(
    (bubble) => bubble.id === selectedId
  );

  const detailBubble = selected || hovered;

  const width = 1120;
  const height = 600;

  const margin = {
    top: 42,
    right: 30,
    bottom: 72,
    left: 64,
  };

  const plotWidth =
    width -
    margin.left -
    margin.right;

  const plotHeight =
    height -
    margin.top -
    margin.bottom;

  function xFor(index) {
    const slot =
      plotWidth /
      STAGES.length;

    return (
      margin.left +
      index * slot +
      slot / 2
    );
  }

  function yFor(percent) {
    return (
      margin.top +
      plotHeight -
      (clamp(percent, 0, 100) / 100) *
        plotHeight
    );
  }

  const totalAmount = bubbles.reduce(
    (total, bubble) =>
      total + bubble.amount,
    0
  );

  const withFinance = bubbles.filter(
    (bubble) =>
      n(bubble.financial?.ingresos) > 0 ||
      n(bubble.financial?.costoNeto ?? bubble.financial?.invertido ?? bubble.financial?.costos) > 0
  ).length;

  const projectsWithoutStage = (proyectos || []).filter(
    (project) =>
      getProjectId(project) &&
      getStageIndex(project) < 0
  ).length;

  const projectsWithoutRoi = bubbles.filter(
    (bubble) =>
      bubble.roi === null ||
      bubble.roi === undefined
  ).length;

  return (
    <main className="pba-page">
      <header className="pba-header">
        <div>
          <span className="pba-eyebrow">
            VISTA DE ANÁLISIS
          </span>

          <h1>
            Burbujas: dinero, avance y rentabilidad de un vistazo
          </h1>
        </div>

        <button
          type="button"
          className="pba-back"
          onClick={onBack}
        >
          Volver a Dirección
        </button>
      </header>

      <section className="pba-board">
        <div className="pba-chart-area">
          <div className="pba-y-title">
            % cobrado
          </div>

          <svg
            className="pba-svg"
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label="Análisis de proyectos por monto, avance de cobro y ROI"
          >
            <line
              x1={margin.left}
              y1={margin.top}
              x2={margin.left}
              y2={margin.top + plotHeight}
              className="pba-axis"
            />

            <line
              x1={margin.left}
              y1={margin.top + plotHeight}
              x2={margin.left + plotWidth}
              y2={margin.top + plotHeight}
              className="pba-axis"
            />
              {/* PCC_BUBBLE_VISUAL_POLISH_V3 */}
              {[25, 50, 75].map((value) => (
                <line
                  key={"grid-" + value}
                  x1={margin.left}
                  y1={yFor(value)}
                  x2={margin.left + plotWidth}
                  y2={yFor(value)}
                  className="pba-grid"
                />
              ))}

            {[0, 25, 50, 75, 100].map((value) => (
              <text
                key={value}
                x={margin.left - 12}
                y={yFor(value) + 5}
                textAnchor="end"
                className="pba-y-label"
              >
                {value}%
              </text>
            ))}

            {STAGES.map((stage, index) => (
              <text
                key={stage.key}
                x={xFor(index)}
                y={margin.top + plotHeight + 30}
                textAnchor="middle"
                className="pba-stage"
              >
                {stage.label}
              </text>
            ))}

            {bubbles.map((bubble, index) => {
              const peers = bubbles.filter(
                (item) =>
                  item.stageIndex ===
                  bubble.stageIndex
              );

              const peerIndex = peers.findIndex(
                (item) =>
                  item.id === bubble.id
              );

              // PCC_BUBBLE_OVERLAP_FIX_V1
              // Spread horizontal dinamico por etapa.
              // Mantiene intacta la posicion vertical real.
              const stageSpacing =
                STAGES.length > 1
                  ? plotWidth / (STAGES.length - 1)
                  : plotWidth;

              const maxSpread =
                Math.max(
                  24,
                  stageSpacing * 0.72
                );

              const peerStep =
                peers.length > 1
                  ? Math.min(
                      42,
                      maxSpread / (peers.length - 1)
                    )
                  : 0;

              const offset =
                peers.length > 1
                  ? (
                      peerIndex -
                      (peers.length - 1) / 2
                    ) * peerStep
                  : 0;

              const densityScale =
                peers.length >= 6
                  ? 0.58
                  : peers.length >= 4
                    ? 0.72
                    : peers.length >= 3
                      ? 0.84
                      : 1;

              const displayRadius =
                Math.max(
                  9,
                  bubble.radius * densityScale
                );

              const cx =
                xFor(
                  bubble.stageIndex
                ) +
                offset;

              const cy =
                yFor(
                  bubble.collected
                );

              return (
                <g
                  key={bubble.id || index}
                  className={
                    "pba-bubble " +
                    (
                      hoveredId === bubble.id
                        ? "is-active "
                        : ""
                    ) +
                    (
                      selectedId === bubble.id
                        ? "is-selected"
                        : ""
                    )
                  }
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setSelectedId((current) =>
                      current === bubble.id
                        ? ""
                        : bubble.id
                    );
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" ||
                      event.key === " "
                    ) {
                      event.preventDefault();
                      setSelectedId((current) =>
                        current === bubble.id
                          ? ""
                          : bubble.id
                      );
                    }
                  }}
                  onMouseEnter={() =>
                    setHoveredId(
                      bubble.id
                    )
                  }
                  onMouseLeave={() =>
                    setHoveredId("")
                  }
                >
                  <circle
                    cx={cx}
                    cy={cy}
                    r={displayRadius}
                      fill={
                        bubble.band.color
                      }
                      opacity={
                        bubble.band?.key === "NO_DATA"
                          ? 0.62
                          : 1
                      }
                  />

                  <title>
                    {bubble.project?.nombre}
                    {"\n"}
                    Monto: {formatMoney(bubble.amount)}
                    {"\n"}
                    Cobrado: {bubble.hasCollected
                      ? formatPercent(bubble.collected)
                      : "Sin datos"}
                    {"\n"}
                    ROI: {formatPercent(bubble.roi)}
                  </title>
                </g>
              );
            })}
          </svg>

          <div className="pba-x-title">
            Etapa del ciclo de vida
          </div>

          {loading && (
            <span className="pba-loading">
              Cargando información financiera…
            </span>
          )}
        </div>

        <aside className="pba-legend">
          <section>
            <h2>
              Rentabilidad del proyecto
            </h2>

            <div className="pba-legend-list">
              {ROI_BANDS.map((band) => (
                <div
                  key={band.key}
                  className="pba-legend-row"
                >
                  <i
                    style={{
                      backgroundColor:
                        band.color,
                    }}
                  />

                  <span>
                    {band.label}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2>
              Tamaño = monto
            </h2>

            <div className="pba-size-scale">
              <i className="pba-size-sm" />
              <i className="pba-size-md" />
              <i className="pba-size-lg" />
            </div>
          </section>

          <section className="pba-reading">
            <p>              <strong>Lectura:</strong>{" "}
              cada burbuja representa un proyecto con etapa definida. La altura indica avance de cobro, el tamaño representa el monto económico y el color representa el ROI. Gris significa que todavía no existe información suficiente para calcular rentabilidad.</p>
          </section>

          <section className="pba-summary">
            <div>
              <span>Proyectos</span>
              <strong>
                {bubbles.length}
              </strong>
            </div>

            <div>
              <span>
                Monto analizado
              </span>
              <strong>
                {formatMdp(totalAmount)}
              </strong>
            </div>

            <div>
              <span>
                Con finanzas
              </span>
              <strong>
                {withFinance}
              </strong>
            </div>
            <div>
              <span>Sin etapa</span>
              <strong>{projectsWithoutStage}</strong>
            </div>

            <div>
              <span>Sin ROI</span>
              <strong>{projectsWithoutRoi}</strong>
            </div>
          </section>
        </aside>

        {detailBubble && (
          <div className="pba-tooltip">
            <span>
              DETALLE DEL PROYECTO
            </span>

            <strong>
              {detailBubble.project?.nombre}
            </strong>

            <dl>
              <div>
                <dt>Etapa</dt>
                <dd>
                  {
                    STAGES[
                      detailBubble.stageIndex
                    ]?.full
                  }
                </dd>
              </div>

              <div>
                <dt>Monto</dt>
                <dd>
                  {formatMdp(
                    detailBubble.amount
                  )}
                </dd>
              </div>

              <div>
                <dt>Cobrado</dt>
                <dd>
                  {detailBubble.hasCollected
                      ? formatPercent(detailBubble.collected)
                      : "Sin datos"}
                </dd>
              </div>

              <div>
                <dt>ROI</dt>
                <dd>
                  {formatPercent(
                    detailBubble.roi
                  )}
                </dd>
              </div>

              <div>
                <dt>Ingresos</dt>
                <dd>
                  {formatMoney(
                    detailBubble.financial
                      ?.ingresos
                  )}
                </dd>
              </div>

              <div>
                <dt>Costo neto</dt>
                <dd>
                  {formatMoney(
                    detailBubble.financial?.costoNeto ??
                      detailBubble.financial?.invertido ??
                      detailBubble.financial?.costos
                  )}
                </dd>
              </div>
            </dl>
          </div>
        )}
      </section>

      {/* UX023A_EXECUTIVE_MODAL */}
      {selected && (
        <div
          className="pba-exec-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedId("");
            }
          }}
        >
          <section
            className="pba-exec-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Expediente ejecutivo del proyecto"
          >
            <header className="pba-exec-header">
              <div className="pba-exec-header-main">
                <div className="pba-exec-eyebrow">
                  IP DEPOT / PORTFOLIO INTELLIGENCE
                </div>

                <h2>
                  {selected.project?.nombre ||
                    selected.project?.name ||
                    "Proyecto"}
                </h2>

                <div className="pba-exec-meta">
                  <span>
                    {STAGES[selected.stageIndex]?.full ||
                      "Etapa no definida"}
                  </span>

                  <span>
                    ID: {selected.id}
                  </span>

                  <span>
                    Consulta financiera de solo lectura
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="pba-exec-close"
                onClick={() => setSelectedId("")}
                aria-label="Cerrar expediente ejecutivo"
              >
                Cerrar
                <span aria-hidden="true">×</span>
              </button>
            </header>

            <div className="pba-exec-body">
              <div className="pba-exec-kpis">
                <article>
                  <span>Monto del proyecto</span>
                  <strong>
                    {executiveMoney(selected.amount)}
                  </strong>
                  <small>Catálogo comercial</small>
                </article>

                <article>
                  <span>Avance de cobro</span>
                  <strong>
                    {selected.hasCollected
                      ? executivePercent(selected.collected)
                      : "Sin datos"}
                  </strong>

                  <div className="pba-exec-progress">
                    <div
                      style={{
                        width:
                          (selected.hasCollected
                            ? selected.collected
                            : 0) + "%"
                      }}
                    />
                  </div>

                  <small>Resumen financiero</small>
                </article>

                <article>
                  <span>Retorno sobre inversión</span>
                  <strong
                    className={
                      selected.roi === null ||
                      selected.roi === undefined
                        ? ""
                        : selected.roi <= 0
                          ? "pba-exec-negative"
                          : "pba-exec-positive"
                    }
                  >
                    {executivePercent(selected.roi)}
                  </strong>
                  <small>Utilidad / costo neto × 100</small>
                </article>

                <article>
                  <span>Costo neto</span>
                  <strong>
                    {executiveMoney(
                      selected.financial?.costoNeto ??
                      selected.financial?.invertido ??
                      selected.financial?.costos
                    )}
                  </strong>
                  <small>Resumen financiero</small>
                </article>
              </div>

              <nav
                className="pba-exec-tabs"
                aria-label="Secciones del expediente"
              >
                <button
                  type="button"
                  className={
                    executiveTab === "resumen" ? "active" : ""
                  }
                  onClick={() => setExecutiveTab("resumen")}
                >
                  Resumen ejecutivo
                </button>

                <button
                  type="button"
                  className={
                    executiveTab === "movimientos" ? "active" : ""
                  }
                  onClick={() => setExecutiveTab("movimientos")}
                >
                  Movimientos financieros
                </button>
              </nav>

              {executiveTab === "resumen" && (
                <div className="pba-exec-content">
                  <section className="pba-exec-panel">
                    <div className="pba-exec-panel-title">
                      <h3>Resultado financiero</h3>
                      <span>Fuente: FIN-019F / Google Sheets</span>
                    </div>

                    <dl className="pba-exec-data">
                      <div>
                        <dt>Ingresos</dt>
                        <dd>
                          {executiveMoney(
                            selected.financial?.ingresos
                          )}
                        </dd>
                      </div>

                      <div>
                        <dt>Costo neto</dt>
                        <dd>
                          {executiveMoney(
                            selected.financial?.costoNeto ??
                            selected.financial?.invertido ??
                            selected.financial?.costos
                          )}
                        </dd>
                      </div>

                      <div>
                        <dt>Utilidad</dt>
                        <dd>
                          {executiveMoney(
                            selected.financial?.utilidad
                          )}
                        </dd>
                      </div>

                      <div>
                        <dt>ROI</dt>
                        <dd>
                          {executivePercent(selected.roi)}
                        </dd>
                      </div>

                      <div>
                        <dt>Avance de cobro</dt>
                        <dd>
                          {selected.hasCollected
                            ? executivePercent(selected.collected)
                            : "Sin datos"}
                        </dd>
                      </div>
                    </dl>
                  </section>

                  <section className="pba-exec-panel">
                    <div className="pba-exec-panel-title">
                      <h3>Información del proyecto</h3>
                      <span>Fuente: catálogo</span>
                    </div>

                    <dl className="pba-exec-data">
                      <div>
                        <dt>Proyecto</dt>
                        <dd>
                          {executiveValue(
                            selected.project?.nombre ??
                            selected.project?.name
                          )}
                        </dd>
                      </div>

                      <div>
                        <dt>Identificador</dt>
                        <dd>{selected.id}</dd>
                      </div>

                      <div>
                        <dt>Etapa comercial</dt>
                        <dd>
                          {STAGES[selected.stageIndex]?.full ||
                            "No registrada"}
                        </dd>
                      </div>

                      <div>
                        <dt>Responsable</dt>
                        <dd>
                          {executiveValue(
                            selected.project?.responsable ??
                            selected.project?.responsableNombre
                          )}
                        </dd>
                      </div>

                      <div>
                        <dt>Cliente</dt>
                        <dd>
                          {executiveValue(
                            selected.project?.cliente ??
                            selected.project?.clienteNombre
                          )}
                        </dd>
                      </div>
                    </dl>
                  </section>
                </div>
              )}

              {executiveTab === "movimientos" && (
                <section className="pba-exec-panel pba-exec-movements">
                  <div className="pba-exec-panel-title">
                    <h3>Movimientos financieros</h3>
                    <span>Consulta a Excel mediante FIN-019F</span>
                  </div>

                  {executiveMovements.loading ? (
                    <div className="pba-exec-empty">
                      Consultando movimientos financieros...
                    </div>
                  ) : executiveMovements.error ? (
                    <div className="pba-exec-empty">
                      {executiveMovements.error}
                    </div>
                  ) : executiveMovements.rows.length === 0 ? (
                    <div className="pba-exec-empty">
                      No existen movimientos financieros registrados
                      para este proyecto.
                    </div>
                  ) : (
                    <>
                      <div className="pba-exec-movement-count">
                        {executiveMovements.rows.length} movimientos
                        recibidos
                      </div>

                      <div className="pba-exec-table-wrap">
                        <table className="pba-exec-table">
                          <thead>
                            <tr>
                              <th>Fecha</th>
                              <th>Concepto</th>
                              <th>Tipo</th>
                              <th>Importe</th>
                            </tr>
                          </thead>

                          <tbody>
                            {executiveMovements.rows.map(
                              (movement, index) => (
                                <tr
                                  key={
                                    movement?.id ??
                                    movement?.movimientoId ??
                                    index
                                  }
                                >
                                  <td>
                                    {executiveValue(
                                      movement?.fecha ??
                                      movement?.fechaMovimiento ??
                                      movement?.fechaPago
                                    )}
                                  </td>

                                  <td>
                                    {executiveValue(
                                      movement?.concepto ??
                                      movement?.descripcion ??
                                      movement?.detalle
                                    )}
                                  </td>

                                  <td>
                                    {executiveValue(
                                      movement?.tipo ??
                                      movement?.tipoMovimiento ??
                                      movement?.categoria
                                    )}
                                  </td>

                                  <td>
                                    {executiveMoney(
                                      movement?.monto ??
                                      movement?.importe ??
                                      movement?.cantidad
                                    )}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </section>
              )}
            </div>

            <footer className="pba-exec-footer">
              <span>
                Datos financieros obtenidos mediante la API
                autenticada del proyecto.
              </span>

              <button
                type="button"
                onClick={() => setSelectedId("")}
              >
                Volver a la gráfica
              </button>
            </footer>
          </section>
        </div>
      )}


    </main>
  );
}

// PCC_PORTFOLIO_BUBBLE_ANALYSIS_V1_1_END